import { useState, useEffect } from 'react'
import axios from 'axios'

import { ApiConfig, ApiPaths } from '@/utils/apiPaths'

interface BasicReviewResponse {
  key: string
  rating: string
  totalReviews: number
  language: string
  url: string
}

interface UseFetchMinimalReviewsParams {
  key: string
  locale?: string
}

interface FetchMinimalReviewsResult {
  reviewData: BasicReviewResponse | null
  isLoading: boolean
  error: string | null
  isSupported: boolean
}

export const useFetchMinimalReviews = ({
  key,
  locale = 'en'
}: UseFetchMinimalReviewsParams): FetchMinimalReviewsResult => {
  const [reviewData, setReviewData] = useState<BasicReviewResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSupported, setIsSupported] = useState(false)

  useEffect(() => {
    if (!key) return

    const isKeySupported = Boolean(
      ApiConfig.PLACE_IDS[key] && ApiConfig.LOCATION_URLS[key]
    )
    setIsSupported(isKeySupported)

    if (!isKeySupported) {
      setIsLoading(false)
      return
    }

    const fetchMinimalReviews = async () => {
      setIsLoading(true)
      try {
        const lang = getLanguageCode(locale)

        const response = await axios.get<BasicReviewResponse>(
          `${ApiPaths.GOOGLE_REVIEWS_MINIMAL}?key=${key}&lang=${lang}`
        )
        setReviewData(response.data)
        setError(null)
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setReviewData(null)
          setError(null)
          return
        }
        console.error('Error fetching minimal reviews:', err)
        setError('Failed to load review data')
        setReviewData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMinimalReviews()
  }, [key, locale])

  return { reviewData, isLoading, error, isSupported }
}

const getLanguageCode = (locale: string): string => {
  switch (locale) {
    case 'pl':
      return 'pl'
    case 'de':
      return 'de'
    default:
      return 'en'
  }
}
